import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { twMerge } from 'tailwind-merge';
import {
  CalendarDays,
  CalendarRange,
  Calendar as CalendarIcon,
  ChevronsLeftRight,
  Diamond,
  Flag,
  LocateFixed,
  Maximize2,
  Minimize2,
  ZoomIn,
} from 'lucide-react';
import { parsePredecessorLinks, resolvePredecessorTaskIndex } from './utils/epcScheduleDependencies';
import {
  getWbsTimelineBarShellClasses,
  getWbsTimelineLabelClasses,
  getWbsTimelineProgressFill,
  normalizeWbsLevel,
} from './utils/epcScheduleWbsLevelStyle';
import { isTaskMilestone, parsePercentComplete } from './utils/epcScheduleMsProjectFormat';
import { WbsTaskTreeGutter } from './WbsTaskTreeGutter';
import type { EpcScheduleTaskRow } from '../types/scheduleTask';

const ROW_H = 28; // compact engraved rows
const BAR_H = 16; // regular task bar height inside a row
const HEADER_H = 50; // sticky two-row date axis (2 × 25px)
const LABEL_W = 300;
const MAX_ROWS = 500;
const DAY_MS = 86400000;

type ZoomKey = 'day' | 'week' | 'month' | 'fit';

const ZOOM_PX_PER_DAY: Record<Exclude<ZoomKey, 'fit'>, number> = {
  day: 28,
  week: 11,
  month: 3.6,
};

const ZOOM_LABEL: Record<ZoomKey, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  fit: 'Fit',
};

/* Dependency-link inks stay on the Sovereign register: bottle, brand, deep champagne, delayed. */
const LINK_COLORS: Record<string, string> = {
  FS: '#1F5A47',
  SS: '#339274',
  FF: '#8A5A08',
  SF: '#9F1239',
};

function parseDay(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const s = String(raw);
  const d = parseISO(s.length >= 10 ? s.slice(0, 10) : s);
  return isValid(d) ? startOfDay(d).getTime() : null;
}

function isWeekend(d: Date): boolean {
  const dow = d.getDay();
  return dow === 0 || dow === 6;
}

function isSummary(task: EpcScheduleTaskRow): boolean {
  const v = task.isSummary;
  return v === true || v === 'true' || v === 1 || v === '1';
}

export type EpcScheduleTimelineProps = {
  tasks: EpcScheduleTaskRow[];
  filteredIndices?: number[];
  selectedIndex: number | null;
  onSelectIndex: (i: number) => void;
};

export default function EpcScheduleTimeline({
  tasks,
  filteredIndices,
  selectedIndex,
  onSelectIndex,
}: EpcScheduleTimelineProps) {
  const [zoom, setZoom] = useState<ZoomKey>('week');
  const [hoverRow, setHoverRow] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [viewportW, setViewportW] = useState(0);
  const [fullView, setFullView] = useState(false);

  useEffect(() => {
    if (!fullView) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullView(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullView]);

  /* Track the chart viewport width for "Fit" zoom. */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth - LABEL_W;
      setViewportW(Math.max(320, w));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const {
    rows,
    minMs,
    rangeDays,
    pxPerDay,
    chartW,
    truncated,
  } = useMemo(() => {
    /* When filteredIndices is provided (from the panel filter bar), render only those rows.
     * row.i stays as the global task index so selectedIndex comparisons remain correct. */
    const sourceIndices = (filteredIndices ?? tasks.map((_, i) => i)).slice(0, MAX_ROWS);
    const slice = sourceIndices.map((i) => tasks[i]!);
    const allMs: number[] = [];
    slice.forEach((task) => {
      const s = parseDay(task.start);
      const e = parseDay(task.finish);
      if (s != null) allMs.push(s);
      if (e != null) allMs.push(e);
    });
    const now = Date.now();
    let minT = allMs.length ? Math.min(...allMs) : now - 14 * DAY_MS;
    let maxT = allMs.length ? Math.max(...allMs) : now + 60 * DAY_MS;

    /* snap min back to start of week, max forward to end of week — keeps axis aligned */
    minT = startOfWeek(new Date(minT), { weekStartsOn: 1 }).getTime();
    maxT = endOfWeek(new Date(maxT), { weekStartsOn: 1 }).getTime();

    /* always include today + a little breathing room */
    const today = startOfDay(new Date()).getTime();
    if (today < minT) minT = today - 7 * DAY_MS;
    if (today > maxT) maxT = today + 7 * DAY_MS;

    const range = Math.max(7, differenceInCalendarDays(new Date(maxT), new Date(minT)) + 1);

    const rowMeta = sourceIndices.map((globalIdx) => {
      const task = tasks[globalIdx]!;
      const s = parseDay(task.start);
      const e = parseDay(task.finish) ?? s;
      return {
        task,
        i: globalIdx,
        s: s ?? minT,
        e: e ?? s ?? minT,
        name: String(task.name ?? '').trim() || '—',
      };
    });

    /* Compute pixels-per-day based on zoom, falling back to "fit" if viewport known */
    let pxPd: number;
    if (zoom === 'fit') {
      pxPd = viewportW > 0 ? Math.max(2, viewportW / range) : 12;
    } else {
      pxPd = ZOOM_PX_PER_DAY[zoom];
    }

    const chartWidth = Math.max(viewportW || 320, range * pxPd + 24);
    const totalCount = filteredIndices?.length ?? tasks.length;

    return {
      rows: rowMeta,
      minMs: minT,
      maxMs: maxT,
      rangeDays: range,
      pxPerDay: pxPd,
      chartW: chartWidth,
      truncated: totalCount > MAX_ROWS,
    };
  }, [tasks, filteredIndices, zoom, viewportW]);

  const barX = (ms: number) => {
    const dayOffset = (ms - minMs) / DAY_MS;
    return Math.max(0, Math.min(rangeDays, dayOffset)) * pxPerDay;
  };

  /* Build month + week tick arrays from snapped range */
  const { monthTicks, dayTicks, weekendBands } = useMemo(() => {
    const months: { startX: number; widthX: number; label: string }[] = [];
    const days: { x: number; date: Date }[] = [];
    const weekends: { x: number; widthX: number }[] = [];

    let cursor = new Date(minMs);
    let monthCursor = startOfMonth(cursor);
    while (monthCursor.getTime() <= minMs + (rangeDays - 1) * DAY_MS) {
      const monthEnd = endOfMonth(monthCursor);
      const startD = monthCursor.getTime() < minMs ? new Date(minMs) : monthCursor;
      const endD = monthEnd.getTime() > minMs + (rangeDays - 1) * DAY_MS
        ? new Date(minMs + (rangeDays - 1) * DAY_MS)
        : monthEnd;
      months.push({
        startX: barX(startD.getTime()),
        widthX: Math.max(8, barX(endD.getTime() + DAY_MS) - barX(startD.getTime())),
        label: format(monthCursor, rangeDays > 240 ? 'MMM yy' : 'MMMM yyyy'),
      });
      monthCursor = startOfMonth(addDays(monthEnd, 1));
    }

    cursor = new Date(minMs);
    for (let i = 0; i <= rangeDays; i++) {
      const d = new Date(minMs + i * DAY_MS);
      days.push({ x: barX(d.getTime()), date: d });
      if (isWeekend(d) && i < rangeDays) {
        weekends.push({ x: barX(d.getTime()), widthX: pxPerDay });
      }
    }

    return { monthTicks: months, dayTicks: days, weekendBands: weekends };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minMs, rangeDays, pxPerDay, chartW]);

  const todayX = useMemo(() => {
    const today = startOfDay(new Date()).getTime();
    if (today < minMs || today > minMs + rangeDays * DAY_MS) return null;
    return barX(today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minMs, rangeDays, pxPerDay]);

  /* Build dependency segments with orthogonal routing */
  const linkSegs = useMemo(() => {
    type LinkSeg = {
      fromRow: number;
      toRow: number;
      code: string;
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    };
    const segs: LinkSeg[] = [];
    rows.forEach((row, rIdx) => {
      const preds = parsePredecessorLinks(row.task.predecessors);
      preds.forEach((p) => {
        const pIdx = resolvePredecessorTaskIndex(p, rows.map((r) => r.task), rIdx);
        if (pIdx == null || pIdx >= rows.length) return;
        const predRow = rows[pIdx]!;
        const code = (p.linkTypeCode || 'FS').toUpperCase();
        const xPredEnd = barX(predRow.e + DAY_MS);
        const xPredStart = barX(predRow.s);
        const xCurrEnd = barX(row.e + DAY_MS);
        const xCurrStart = barX(row.s);
        let x1: number, x2: number;
        if (code === 'SS') {
          x1 = xPredStart;
          x2 = xCurrStart;
        } else if (code === 'FF') {
          x1 = xPredEnd;
          x2 = xCurrEnd;
        } else if (code === 'SF') {
          x1 = xPredStart;
          x2 = xCurrEnd;
        } else {
          x1 = xPredEnd;
          x2 = xCurrStart;
        }
        segs.push({
          fromRow: pIdx,
          toRow: rIdx,
          code,
          x1,
          y1: pIdx * ROW_H + ROW_H / 2,
          x2,
          y2: rIdx * ROW_H + ROW_H / 2,
        });
      });
    });
    return segs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, pxPerDay, minMs, rangeDays]);

  /* Stats */
  const stats = useMemo(() => {
    let milestones = 0;
    let summaries = 0;
    let leaves = 0;
    let progressSum = 0;
    let progressDenom = 0;
    rows.forEach((r) => {
      if (isTaskMilestone(r.task)) milestones += 1;
      if (isSummary(r.task)) summaries += 1;
      else {
        leaves += 1;
        const p = parsePercentComplete(r.task.percentComplete);
        if (p != null) {
          progressSum += p;
          progressDenom += 1;
        }
      }
    });
    const avg = progressDenom > 0 ? Math.round(progressSum / progressDenom) : 0;
    return { milestones, summaries, leaves, avg };
  }, [rows]);

  const projectStart = rows.length ? new Date(Math.min(...rows.map((r) => r.s))) : null;
  const projectEnd = rows.length ? new Date(Math.max(...rows.map((r) => r.e))) : null;
  const projectSpan =
    projectStart && projectEnd
      ? differenceInCalendarDays(projectEnd, projectStart) + 1
      : 0;

  /* Auto-scroll to today on mount / zoom change */
  const didInitialScroll = useRef(false);
  useEffect(() => {
    if (didInitialScroll.current) return;
    if (!scrollRef.current || todayX == null) return;
    didInitialScroll.current = true;
    scrollRef.current.scrollLeft = Math.max(0, todayX - 120);
  }, [todayX]);

  const scrollToToday = () => {
    if (!scrollRef.current || todayX == null) return;
    scrollRef.current.scrollTo({ left: Math.max(0, todayX - 200), behavior: 'smooth' });
  };

  const fitToView = () => {
    setZoom('fit');
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  /* === RENDER === */
  return (
    <>
      {fullView ? (
        <div
          className="fixed inset-0 z-40 bg-[#2A1F17]/45 backdrop-blur-sm"
          onClick={() => setFullView(false)}
          aria-hidden
        />
      ) : null}
      <motion.div
        layout
        className={twMerge(
          'overflow-hidden rounded-2xl border border-[rgba(11,59,46,0.35)] bg-[rgba(250,247,238,0.96)] shadow-[0_18px_44px_-20px_rgba(42,31,23,0.40)]',
          fullView && 'fixed inset-4 z-50 flex flex-col rounded-2xl ring-2 ring-[rgba(201,169,97,0.45)]'
        )}
      >
      {/* Champagne keyline — engraved top rule */}
      <div aria-hidden className="h-[2px] w-full shrink-0 bg-[#C9A961]/70" />
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2 border-b border-[rgba(11,59,46,0.14)] bg-[#F8F2E2]/80 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#0B3B2E] text-[#F8F2E2] shadow-sm ring-1 ring-[rgba(201,169,97,0.5)]">
            <CalendarRange className="h-3.5 w-3.5" strokeWidth={2.25} />
          </span>
          <div>
            <h3 className="text-[13px] font-extrabold leading-tight tracking-tight text-[#0B3B2E]">
              Schedule timeline
            </h3>
            <p className="text-[10px] leading-tight text-[#7C8A82]">
              Gantt view ·{' '}
              <span className="font-mono font-semibold tabular-nums text-[#2A1F17]">
                {rows.length}
              </span>
              {filteredIndices != null && filteredIndices.length !== tasks.length ? (
                <span className="text-[#7C8A82]">
                  {' '}/{' '}
                  <span className="font-mono tabular-nums">{tasks.length}</span>
                </span>
              ) : null}{' '}
              tasks
              {projectStart && projectEnd ? (
                <>
                  {' · '}
                  <span className="font-medium text-[#2A1F17]">
                    {format(projectStart, 'MMM d, yyyy')}
                  </span>
                  <span className="mx-0.5 text-[#7C8A82]">→</span>
                  <span className="font-medium text-[#2A1F17]">
                    {format(projectEnd, 'MMM d, yyyy')}
                  </span>
                  <span className="ml-1 text-[#7C8A82]">({projectSpan}d)</span>
                </>
              ) : null}
            </p>
          </div>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {/* KPI mini chips */}
          {stats.milestones > 0 ? (
            <span
              className="inline-flex items-center gap-1 rounded-md border border-[rgba(201,169,97,0.5)] bg-[rgba(201,169,97,0.14)] px-1.5 py-0.5 text-[10.5px] font-medium text-[#8A5A08]"
              title={`${stats.milestones} milestone${stats.milestones === 1 ? '' : 's'}`}
            >
              <Diamond className="h-3 w-3 fill-[#C9A961] text-[#8A5A08]" />
              <span className="font-bold tabular-nums">{stats.milestones}</span>
            </span>
          ) : null}
          {stats.summaries > 0 ? (
            <span
              className="inline-flex items-center gap-1 rounded-md border border-[rgba(31,90,71,0.30)] bg-[rgba(31,90,71,0.08)] px-1.5 py-0.5 text-[10.5px] font-medium text-[#1F5A47]"
              title={`${stats.summaries} summary task${stats.summaries === 1 ? '' : 's'}`}
            >
              <Flag className="h-3 w-3 text-[#1F5A47]" />
              <span className="font-bold tabular-nums">{stats.summaries}</span>
            </span>
          ) : null}
          <span
            className="inline-flex items-center gap-1.5 rounded-md border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-2 py-0.5 text-[10.5px] font-medium text-[#2A1F17]"
            title={`Average % complete across ${stats.leaves} leaf task${stats.leaves === 1 ? '' : 's'}`}
          >
            <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#7C8A82]">Avg</span>
            <span className="relative h-1.5 w-16 overflow-hidden rounded-full bg-[#E7DCC4] ring-1 ring-[rgba(11,59,46,0.10)]">
              <span
                className="absolute inset-y-0 left-0 rounded-full bg-[#339274]"
                style={{ width: `${Math.max(2, stats.avg)}%` }}
              />
            </span>
            <span className="font-mono text-[10px] font-bold tabular-nums text-[#0B3B2E]">
              {stats.avg}%
            </span>
          </span>

          {/* Zoom segment */}
          <div
            role="group"
            aria-label="Timeline zoom"
            className="inline-flex h-7 items-stretch overflow-hidden rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] shadow-sm"
          >
            {(['day', 'week', 'month', 'fit'] as const).map((z) => (
              <button
                key={z}
                type="button"
                onClick={() => {
                  setZoom(z);
                  if (z === 'fit' && scrollRef.current) {
                    scrollRef.current.scrollLeft = 0;
                  }
                }}
                aria-pressed={zoom === z}
                className={twMerge(
                  'inline-flex items-center gap-1 px-2 text-[11px] font-semibold transition border-l first:border-l-0 border-[rgba(11,59,46,0.14)]',
                  zoom === z
                    ? 'bg-[#264f3c] text-white'
                    : 'text-[#0B3B2E] hover:bg-[rgba(11,59,46,0.05)]'
                )}
                title={
                  z === 'day'
                    ? 'Day view (28 px / day)'
                    : z === 'week'
                      ? 'Week view (11 px / day)'
                      : z === 'month'
                        ? 'Month view (~4 px / day)'
                        : 'Fit timeline to width'
                }
              >
                {z === 'day' ? (
                  <CalendarDays className="h-3 w-3" />
                ) : z === 'week' ? (
                  <CalendarIcon className="h-3 w-3" />
                ) : z === 'month' ? (
                  <ZoomIn className="h-3 w-3" />
                ) : (
                  <ChevronsLeftRight className="h-3 w-3" />
                )}
                {ZOOM_LABEL[z]}
              </button>
            ))}
          </div>

          {/* Today */}
          <button
            type="button"
            onClick={scrollToToday}
            disabled={todayX == null}
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[rgba(185,28,28,0.35)] bg-[rgba(185,28,28,0.06)] px-2 text-[11px] font-semibold text-[#9F1239] shadow-sm transition hover:bg-[rgba(185,28,28,0.10)] disabled:opacity-40"
            title="Scroll to today"
          >
            <LocateFixed className="h-3 w-3" />
            Today
          </button>

          {/* Fit shortcut */}
          <button
            type="button"
            onClick={fitToView}
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] px-2 text-[11px] font-semibold text-[#0B3B2E] shadow-sm transition hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)]"
            title="Fit timeline to viewport"
          >
            <Maximize2 className="h-3 w-3" />
            Fit
          </button>

          {/* Full view toggle */}
          <button
            type="button"
            onClick={() => setFullView((v) => !v)}
            className={twMerge(
              'inline-flex h-7 items-center gap-1 rounded-lg border px-2 text-[11px] font-semibold shadow-sm transition',
              fullView
                ? 'border-[rgba(11,59,46,0.45)] bg-[#264f3c] text-white hover:bg-[#2d5c47]'
                : 'border-[rgba(11,59,46,0.28)] bg-[#fcfaf3] text-[#0B3B2E] hover:border-[rgba(51,146,116,0.6)] hover:bg-[rgba(11,59,46,0.04)]'
            )}
            title={fullView ? 'Exit full view' : 'Expand to full view'}
            aria-label={fullView ? 'Exit full view' : 'Expand to full view'}
          >
            {fullView ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* Truncation banner */}
      {truncated ? (
        <div className="flex items-center gap-2 border-b border-[rgba(201,169,97,0.45)] bg-[rgba(201,169,97,0.12)] px-3 py-1.5 text-[10.5px] font-medium text-[#8A5A08]">
          <Flag className="h-3 w-3" />
          Showing first {MAX_ROWS} of {filteredIndices?.length ?? tasks.length} tasks. Use filters to drill in.
        </div>
      ) : null}

      {/* Body: scrollable horizontally and vertically */}
      <div
        ref={scrollRef}
        className={twMerge(
          'relative overflow-auto bg-[#FCFBF6] [scrollbar-gutter:stable]',
          fullView ? 'flex-1' : 'max-h-[min(72vh,720px)]'
        )}
      >
        <div className="flex" style={{ minWidth: LABEL_W + chartW }}>
          {/* === LEFT: sticky labels === */}
          <div
            className="sticky left-0 z-30 shrink-0 border-r border-[rgba(11,59,46,0.18)] bg-[#FCFBF6]"
            style={{ width: LABEL_W }}
          >
            {/* Sticky header label aligned with date axis */}
            <div
              className="sticky top-0 z-10 border-b border-[rgba(11,59,46,0.14)] bg-[#F8F2E2] px-3 flex items-end pb-1.5 shadow-[0_1px_0_rgba(201,169,97,0.4)]"
              style={{ height: HEADER_H }}
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#2A1F17]">
                Task
              </span>
            </div>
            {/* Rows */}
            {rows.map((row) => {
              const wbsL = normalizeWbsLevel(row.task.outlineLevel);
              const isSel = selectedIndex === row.i;
              const isHover = hoverRow === row.i;
              return (
                <button
                  key={row.i}
                  type="button"
                  onClick={() => onSelectIndex(row.i)}
                  onMouseEnter={() => setHoverRow(row.i)}
                  onMouseLeave={() => setHoverRow((h) => (h === row.i ? null : h))}
                  className={twMerge(
                    getWbsTimelineLabelClasses(wbsL, isSel),
                    'group/row text-[11px]',
                    isSel ? 'text-[#0B3B2E]' : 'text-[#2A1F17]',
                    isHover && !isSel ? 'bg-[rgba(11,59,46,0.04)]' : ''
                  )}
                  style={{ height: ROW_H }}
                >
                  <WbsTaskTreeGutter level={wbsL} className="min-h-0" />
                  <span className="flex min-w-0 flex-1 items-center gap-1.5 pl-1.5 pr-2 leading-tight">
                    {isTaskMilestone(row.task) ? (
                      <Diamond className="h-3 w-3 shrink-0 fill-[#C9A961] text-[#8A5A08]" />
                    ) : isSummary(row.task) ? (
                      <Flag className="h-3 w-3 shrink-0 text-[#1F5A47]" />
                    ) : null}
                    <span className="truncate">{row.name}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* === RIGHT: chart === */}
          <div className="relative shrink-0" style={{ width: chartW }}>
            {/* Sticky two-row date axis */}
            <div
              className="sticky top-0 z-20 border-b border-[rgba(11,59,46,0.14)] bg-[#F8F2E2] shadow-[0_1px_0_rgba(201,169,97,0.4)]"
              style={{ width: chartW, height: HEADER_H }}
            >
              {/* Month row */}
              <div className="relative h-[25px] border-b border-[rgba(11,59,46,0.10)]">
                {monthTicks.map((m, idx) => (
                  <div
                    key={`${m.label}-${idx}`}
                    className="absolute top-0 flex h-full items-center border-r border-[rgba(11,59,46,0.10)] px-1.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-[#0B3B2E]"
                    style={{ left: m.startX, width: m.widthX }}
                  >
                    <span className="truncate">{m.label}</span>
                  </div>
                ))}
              </div>
              {/* Day / Week sub-row */}
              <div className="relative h-[25px]">
                {dayTicks.map((d, i) => {
                  const dow = d.date.getDay();
                  const isWk = dow === 0 || dow === 6;
                  const isMonStart = dow === 1; // start of work week
                  const showLabel =
                    pxPerDay >= 24 ||
                    (pxPerDay >= 8 && isMonStart) ||
                    (pxPerDay >= 4 && d.date.getDate() === 1);
                  return (
                    <div
                      key={i}
                      className={twMerge(
                        'absolute top-0 flex h-full items-center justify-center text-[9.5px] tabular-nums',
                        isWk ? 'text-[#7C8A82]/70' : 'text-[#2A1F17]/80',
                        isMonStart ? 'border-l border-[rgba(11,59,46,0.16)]' : 'border-l border-[rgba(11,59,46,0.06)]'
                      )}
                      style={{ left: d.x, width: pxPerDay }}
                    >
                      {showLabel ? (
                        <span className="truncate font-medium">
                          {pxPerDay >= 24
                            ? format(d.date, 'd')
                            : pxPerDay >= 8
                              ? format(d.date, 'd')
                              : format(d.date, 'MMM')}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Body chart area */}
            <div
              className="relative"
              style={{ width: chartW, height: rows.length * ROW_H }}
            >
              {/* Weekend bands */}
              {pxPerDay >= 6
                ? weekendBands.map((wb, i) => (
                    <div
                      key={i}
                      className="absolute top-0 bottom-0 bg-[rgba(11,59,46,0.04)]"
                      style={{ left: wb.x, width: wb.widthX }}
                      aria-hidden
                    />
                  ))
                : null}

              {/* Vertical day grid lines (week-start emphasis) */}
              {pxPerDay >= 4
                ? dayTicks.map((d, i) => {
                    if (d.x > chartW) return null;
                    const dow = d.date.getDay();
                    const isMonStart = dow === 1;
                    if (!isMonStart && pxPerDay < 12) return null;
                    return (
                      <div
                        key={i}
                        className={twMerge(
                          'absolute top-0 bottom-0 w-px',
                          isMonStart ? 'bg-[rgba(11,59,46,0.10)]' : 'bg-[rgba(11,59,46,0.05)]'
                        )}
                        style={{ left: d.x }}
                        aria-hidden
                      />
                    );
                  })
                : null}

              {/* Today line */}
              {todayX != null ? (
                <div
                  className="absolute top-0 bottom-0 z-[5] w-px bg-[#B91C1C]"
                  style={{ left: todayX }}
                  aria-hidden
                >
                  <span className="absolute -top-px -translate-x-1/2 rounded-b bg-[#B91C1C] px-1 py-px text-[8.5px] font-bold uppercase tracking-wider text-white shadow-sm">
                    Today
                  </span>
                </div>
              ) : null}

              {/* Row separators + hover highlights */}
              {rows.map((row, idx) => {
                const isSel = selectedIndex === row.i;
                const isHover = hoverRow === row.i;
                return (
                  <div
                    key={row.i}
                    className={twMerge(
                      'absolute left-0 right-0 border-b border-[rgba(11,59,46,0.06)]',
                      idx % 2 === 1 ? 'bg-[rgba(11,59,46,0.02)]' : '',
                      isHover ? 'bg-[rgba(51,146,116,0.07)]' : '',
                      isSel ? 'bg-[rgba(201,169,97,0.12)]' : ''
                    )}
                    style={{ top: idx * ROW_H, height: ROW_H }}
                    onMouseEnter={() => setHoverRow(row.i)}
                    onMouseLeave={() => setHoverRow((h) => (h === row.i ? null : h))}
                    onClick={() => onSelectIndex(row.i)}
                    aria-hidden
                  />
                );
              })}

              {/* Dependency arrows (orthogonal) */}
              <svg
                className="pointer-events-none absolute left-0 top-0 z-[6]"
                width={chartW}
                height={rows.length * ROW_H}
                aria-hidden
              >
                <defs>
                  {(['FS', 'SS', 'FF', 'SF'] as const).map((c) => (
                    <marker
                      key={c}
                      id={`tl-arr-${c}`}
                      markerWidth="8"
                      markerHeight="8"
                      refX="6.5"
                      refY="3"
                      orient="auto"
                      markerUnits="strokeWidth"
                    >
                      <path d="M0,0 L7,3 L0,6 Z" fill={LINK_COLORS[c]} fillOpacity={0.95} />
                    </marker>
                  ))}
                </defs>
                {linkSegs.map((ln, k) => {
                  const col = LINK_COLORS[ln.code] ?? LINK_COLORS.FS;
                  const goingDown = ln.y2 > ln.y1;
                  /* Orthogonal route: x1,y1 → x1+8,y1 → x1+8,midY → x2-8,midY → x2-8,y2 → x2,y2 */
                  const stub = 8;
                  const fromX = ln.x1 + (ln.code === 'SS' || ln.code === 'SF' ? -stub : stub);
                  const toX = ln.x2 + (ln.code === 'FF' || ln.code === 'SF' ? stub : -stub);
                  const midY = ln.y1 + (goingDown ? ROW_H / 2 + 2 : -(ROW_H / 2 + 2));
                  /* Path with corner radius for nicer look */
                  const r = 4;
                  const d = [
                    `M ${ln.x1} ${ln.y1}`,
                    `L ${fromX - Math.sign(fromX - ln.x1) * r} ${ln.y1}`,
                    `Q ${fromX} ${ln.y1} ${fromX} ${ln.y1 + Math.sign(midY - ln.y1) * r}`,
                    `L ${fromX} ${midY - Math.sign(midY - ln.y1) * r}`,
                    `Q ${fromX} ${midY} ${fromX + Math.sign(toX - fromX) * r} ${midY}`,
                    `L ${toX - Math.sign(toX - fromX) * r} ${midY}`,
                    `Q ${toX} ${midY} ${toX} ${midY + Math.sign(ln.y2 - midY) * r}`,
                    `L ${toX} ${ln.y2 - Math.sign(ln.y2 - midY) * r}`,
                    `Q ${toX} ${ln.y2} ${toX + Math.sign(ln.x2 - toX) * r} ${ln.y2}`,
                    `L ${ln.x2} ${ln.y2}`,
                  ].join(' ');
                  const isFlash =
                    hoverRow != null && (hoverRow === ln.fromRow || hoverRow === ln.toRow);
                  return (
                    <path
                      key={k}
                      d={d}
                      fill="none"
                      stroke={col}
                      strokeWidth={isFlash ? 2.2 : 1.4}
                      strokeOpacity={isFlash ? 1 : 0.7}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      markerEnd={`url(#tl-arr-${ln.code in LINK_COLORS ? ln.code : 'FS'})`}
                    />
                  );
                })}
              </svg>

              {/* Bars */}
              {rows.map((row, idx) => {
                const x0 = barX(row.s);
                const x1 = barX(row.e + DAY_MS); // +1 day so finish day is included
                const w = Math.max(8, x1 - x0);
                const left = x0;
                const pct = parsePercentComplete(row.task.percentComplete) ?? 0;
                const wbsL = normalizeWbsLevel(row.task.outlineLevel);
                const isSum = isSummary(row.task);
                const isMs = isTaskMilestone(row.task);
                const isSel = selectedIndex === row.i;
                const isHover = hoverRow === row.i;
                const fill = getWbsTimelineProgressFill(wbsL);

                const startStr = format(new Date(row.s), 'MMM d, yyyy');
                const finishStr = format(new Date(row.e), 'MMM d, yyyy');
                const days = differenceInCalendarDays(new Date(row.e), new Date(row.s)) + 1;
                const tooltip =
                  `${row.name}\n${startStr} → ${finishStr} · ${days}d` +
                  (isMs ? ' · Milestone' : isSum ? ' · Summary' : '') +
                  (pct > 0 ? ` · ${pct}%` : '');

                /* Milestone diamond */
                if (isMs) {
                  const cx = barX(row.s);
                  return (
                    <button
                      key={row.i}
                      type="button"
                      onClick={() => onSelectIndex(row.i)}
                      onMouseEnter={() => setHoverRow(row.i)}
                      onMouseLeave={() => setHoverRow((h) => (h === row.i ? null : h))}
                      title={tooltip}
                      className="group/bar absolute z-[8] focus:outline-none"
                      style={{
                        left: cx - 6,
                        top: idx * ROW_H + ROW_H / 2 - 6,
                        width: 12,
                        height: 12,
                      }}
                      aria-label={tooltip}
                    >
                      <span
                        className={twMerge(
                          'block h-3 w-3 rotate-45 rounded-[2px] bg-[#C9A961] ring-2 ring-white shadow-sm transition-transform',
                          (isSel || isHover) && 'scale-110 ring-[rgba(201,169,97,0.5)]',
                          isSel && 'ring-2 ring-[#0B3B2E]'
                        )}
                      />
                    </button>
                  );
                }

                /* Summary bar (caret-end shape) */
                if (isSum) {
                  return (
                    <motion.button
                      key={row.i}
                      type="button"
                      onClick={() => onSelectIndex(row.i)}
                      onMouseEnter={() => setHoverRow(row.i)}
                      onMouseLeave={() => setHoverRow((h) => (h === row.i ? null : h))}
                      title={tooltip}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.18, delay: idx * 0.006 }}
                      className="absolute z-[8] focus:outline-none"
                      style={{
                        left,
                        width: w,
                        height: BAR_H,
                        top: idx * ROW_H + (ROW_H - BAR_H) / 2,
                      }}
                      aria-label={tooltip}
                    >
                      <svg
                        width={w}
                        height={BAR_H}
                        viewBox={`0 0 ${w} ${BAR_H}`}
                        preserveAspectRatio="none"
                        className={twMerge(
                          'overflow-visible',
                          (isSel || isHover) && 'drop-shadow'
                        )}
                      >
                        {/* Racing-green ink summary bar */}
                        <rect
                          x={0}
                          y={3}
                          width={w}
                          height={5}
                          rx={1.5}
                          fill="#0B3B2E"
                          opacity={isSel || isHover ? 1 : 0.92}
                        />
                        {/* Left caret */}
                        <path
                          d={`M 0 8 L 5 8 L 0 14 Z`}
                          fill="#0B3B2E"
                        />
                        {/* Right caret */}
                        <path
                          d={`M ${w} 8 L ${w - 5} 8 L ${w} 14 Z`}
                          fill="#0B3B2E"
                        />
                        {/* Champagne progress overlay (inset) */}
                        {pct > 0 ? (
                          <rect
                            x={1}
                            y={4}
                            width={Math.max(2, ((w - 2) * pct) / 100)}
                            height={3}
                            rx={1}
                            fill="#C9A961"
                            opacity={0.95}
                          />
                        ) : null}
                      </svg>
                    </motion.button>
                  );
                }

                /* Regular task bar */
                return (
                  <motion.button
                    key={row.i}
                    type="button"
                    onClick={() => onSelectIndex(row.i)}
                    onMouseEnter={() => setHoverRow(row.i)}
                    onMouseLeave={() => setHoverRow((h) => (h === row.i ? null : h))}
                    title={tooltip}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: Math.min(idx * 0.006, 0.4) }}
                    className={twMerge(
                      'group/bar absolute z-[8] focus:outline-none',
                      getWbsTimelineBarShellClasses(wbsL, false),
                      isSel
                        ? 'ring-2 ring-[#C9A961] shadow-md z-[9]'
                        : isHover
                          ? 'ring-1 ring-[rgba(51,146,116,0.55)] shadow-sm'
                          : ''
                    )}
                    style={{
                      left,
                      width: w,
                      height: BAR_H,
                      top: idx * ROW_H + (ROW_H - BAR_H) / 2,
                    }}
                    aria-label={tooltip}
                  >
                    {/* Flat progress fill (engraved ink, no gradient) */}
                    <motion.div
                      className={twMerge('h-full', fill)}
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.45, delay: 0.1 + idx * 0.005, ease: 'easeOut' }}
                    />
                    {/* Bar text label (only when wide) */}
                    {w > 70 ? (
                      <span className="pointer-events-none absolute inset-y-0 left-1.5 right-1 flex items-center truncate text-[9.5px] font-semibold text-[#0B3B2E] [text-shadow:0_0_3px_rgba(252,250,243,0.95),0_0_1px_rgba(252,250,243,0.95)]">
                        <span className="truncate">{row.name}</span>
                        {pct > 0 ? (
                          <span className="ml-auto pl-1 font-mono text-[9px] text-[#2A1F17]/70">
                            {pct}%
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                  </motion.button>
                );
              })}

              {/* Empty state */}
              {rows.length === 0 ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
                  <CalendarRange className="h-8 w-8 text-[rgba(11,59,46,0.25)]" />
                  {filteredIndices != null && tasks.length > 0 ? (
                    <>
                      <p className="text-sm font-medium text-[#2A1F17]">No tasks match current filters</p>
                      <p className="text-[11px] text-[#7C8A82]">Adjust the filters above to see tasks on the timeline.</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-[#2A1F17]">No tasks to plot</p>
                      <p className="text-[11px] text-[#7C8A82]">Import an MPP file or create tasks to populate the timeline.</p>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Footer legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[rgba(11,59,46,0.14)] bg-[#F8F2E2]/70 px-3 py-1.5 text-[10px] text-[#7C8A82]">
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-3 rounded-sm bg-[#339274]" />
          Task
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-3 rounded-sm bg-[#0B3B2E]" />
          Summary
        </span>
        <span className="inline-flex items-center gap-1">
          <Diamond className="h-2.5 w-2.5 fill-[#C9A961] text-[#8A5A08]" />
          Milestone
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-3 w-px bg-[#B91C1C]" />
          <span className="text-[#9F1239]">Today</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-3 rounded-sm bg-[rgba(11,59,46,0.06)] ring-1 ring-[rgba(11,59,46,0.12)]" />
          Weekend
        </span>
        <span className="ml-auto inline-flex items-center gap-2">
          {(['FS', 'SS', 'FF', 'SF'] as const).map((c) => (
            <span key={c} className="inline-flex items-center gap-1" title={c}>
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: LINK_COLORS[c] }}
              />
              <span className="font-mono text-[9.5px] font-bold tabular-nums text-[#2A1F17]/70">
                {c}
              </span>
            </span>
          ))}
        </span>
      </div>
      </motion.div>
    </>
  );
}

export function buildCanonicalPayload(
  full: Record<string, unknown> | null | undefined,
  taskRows: EpcScheduleTaskRow[]
): Record<string, unknown> {
  const stats = {
    taskCount: taskRows.length,
    maxOutlineLevel: taskRows.reduce((m, t) => Math.max(m, Number(t.outlineLevel) || 0), 0),
  };
  if (full && typeof full === 'object') {
    const prev = (full.stats as Record<string, unknown> | undefined) ?? {};
    return {
      ...full,
      scheduleTaskDomain: full['scheduleTaskDomain'] ?? 'epc',
      tasks: taskRows,
      stats: { ...prev, ...stats },
    };
  }
  return {
    formatVersion: 1,
    scheduleTaskDomain: 'epc',
    tasks: taskRows,
    project: {},
    stats,
    warnings: [],
    taskTree: [],
  };
}

export function downloadTasksCsv(taskRows: EpcScheduleTaskRow[], filename: string) {
  const headers = [
    'uniqueId',
    'name',
    'outlineLevel',
    'epcOrgUnitId',
    'assignedUserId',
    'start',
    'finish',
    'percentComplete',
    'isSummary',
  ];
  const lines = [headers.join(',')];
  for (const t of taskRows) {
    const tr = t as Record<string, unknown>;
    const row = headers.map((h) => {
      const v = tr[h];
      const s = v == null ? '' : String(v).replace(/"/g, '""');
      return `"${s}"`;
    });
    lines.push(row.join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}
